// ─────────────────────────────────────────────────────────────────────────────
// V2TDS — /dashboard/tdsreceivable in the v2 design.
// Endpoints are v1's: GET /api/tds/receivable, PUT /api/tds/certificate/:id
// FY + quarter filtering identical to v1 (FY starts in April; entry.quarter).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useMemo, useState } from "react";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import API_BASE from "../apiConfig";
import { Glyph, Duo } from "./icons";
import { Money, Ring, Segmented, Sheet, Skel, inr } from "./primitives";
import { Field, PageHead } from "./kit";

const auth = () => ({ "auth-token": localStorage.getItem("token") });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");
const fyOf = (d) => { const x = new Date(d); return x.getMonth() >= 3 ? x.getFullYear() : x.getFullYear() - 1; };
const currentFY = () => fyOf(new Date());
const fyLabel = (y) => `FY ${y}–${String(y + 1).slice(-2)}`;
const QUARTERS = [
  { key: "Q1", period: "Apr – Jun", due: "Form 16A by 15 Aug" },
  { key: "Q2", period: "Jul – Sep", due: "Form 16A by 15 Nov" },
  { key: "Q3", period: "Oct – Dec", due: "Form 16A by 15 Feb" },
  { key: "Q4", period: "Jan – Mar", due: "Form 16A by 15 Jun" },
];

export default function V2TDS({ showAlert }) {
  usePageTitle("TDS Receivable");
  const { hasPermission } = useContext(userContext);
  const canWrite = hasPermission("tds", "write");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [fy, setFy] = useState(currentFY());
  const [quarter, setQuarter] = useState("all");
  const [certFilter, setCertFilter] = useState("all");
  const [certFor, setCertFor] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/tds/receivable`, { headers: auth() });
      const d = await r.json();
      if (!r.ok) throw new Error();
      setEntries(d.entries || []);
    } catch { setErr("Couldn't load TDS entries."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const inFY = useMemo(() => entries.filter((e) => fyOf(e.paymentDate) === fy), [entries, fy]);
  const qTotals = useMemo(() => Object.fromEntries(QUARTERS.map((q) => [q.key, inFY.filter((e) => e.quarter === q.key)])), [inFY]);
  const shown = inFY
    .filter((e) => quarter === "all" || e.quarter === quarter)
    .filter((e) => certFilter === "all" || (certFilter === "pending" ? !e.certificateReceived : e.certificateReceived));
  const scope = inFY.filter((e) => quarter === "all" || e.quarter === quarter);
  const total = scope.reduce((s, e) => s + (e.tdsAmount || 0), 0);
  const got = scope.filter((e) => e.certificateReceived).length;
  const pending = scope.length - got;
  const pct = scope.length ? Math.round((got / scope.length) * 100) : 0;
  const pendingAmt = scope.filter((e) => !e.certificateReceived).reduce((s, e) => s + (e.tdsAmount || 0), 0);
  const thisFY = currentFY();

  return (
    <div className="v2p v2t">
      <PageHead eyebrow="Receivables" title="TDS receivable"
        sub="Tax your clients deducted at source — claim it once their Form 16A certificate arrives."
        actions={
          <div className="v2-stepper v2-glass" role="group" aria-label="Financial year">
            <button type="button" aria-label="Previous year" onClick={() => setFy((y) => y - 1)}><Glyph name="chevronLeft" size={16} stroke={2} /></button>
            <span className="v2-stepper-label">{fyLabel(fy)}</span>
            <button type="button" aria-label="Next year" disabled={fy >= thisFY} onClick={() => setFy((y) => y + 1)}><Glyph name="chevronRight" size={16} stroke={2} /></button>
          </div>
        } />

      {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}<button type="button" className="v2-btn v2-btn--sm" onClick={load}>Retry</button></div>}

      <div className="v2t-top">
        <section className="v2-card v2t-hero">
          <div>
            <span className="v2-label">{quarter === "all" ? `Total TDS · ${fyLabel(fy)}` : `${quarter} · ${QUARTERS.find((q) => q.key === quarter).period}`}</span>
            <div className="v2t-hero-amt">{loading ? <Skel h={40} w={200} /> : <Money value={total} />}</div>
            <span className="v2-row-sub">{scope.length} deduction{scope.length === 1 ? "" : "s"} · {inr(pendingAmt)} awaiting certificates</span>
          </div>
          <Ring value={pct} size={104} stroke={10} color="var(--v2-good)" label={`${pct}% certificates received`}>
            <strong>{got}/{scope.length}</strong><span>certificates</span>
          </Ring>
        </section>
        <div className="v2t-quarters" role="tablist" aria-label="Quarter">
          <button type="button" role="tab" aria-selected={quarter === "all"} className={`v2t-q${quarter === "all" ? " is-on" : ""}`} onClick={() => setQuarter("all")}>
            <span className="v2t-q-key">Full year</span>
            <strong>{inr(inFY.reduce((s, e) => s + (e.tdsAmount || 0), 0))}</strong>
            <span className="v2t-q-sub">{inFY.length} entries</span>
          </button>
          {QUARTERS.map((q) => {
            const list = qTotals[q.key] || [];
            const missing = list.filter((e) => !e.certificateReceived).length;
            return (
              <button key={q.key} type="button" role="tab" aria-selected={quarter === q.key}
                className={`v2t-q${quarter === q.key ? " is-on" : ""}`} onClick={() => setQuarter(q.key)}>
                <span className="v2t-q-key">{q.key} <span>{q.period}</span></span>
                <strong>{inr(list.reduce((s, e) => s + (e.tdsAmount || 0), 0))}</strong>
                <span className="v2t-q-sub">{list.length ? (missing ? `${missing} certificate${missing === 1 ? "" : "s"} pending` : "All certificates in") : "No deductions"}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="v2i-toolbar">
        <Segmented size="sm" ariaLabel="Certificate status" value={certFilter} onChange={setCertFilter}
          options={[{ value: "all", label: "All" }, { value: "pending", label: `Pending${pending ? ` · ${pending}` : ""}` }, { value: "received", label: "Received" }]} />
      </div>

      <section className="v2-card v2i-card">
        {loading ? <div className="v2-list" style={{ padding: 8 }}>{[0, 1, 2, 3].map((i) => <Skel key={i} h={58} r={16} />)}</div>
          : shown.length === 0 ? (
            <div className="v2c-empty"><Duo name="percent" size={44} /><strong>No TDS here</strong>
              <span>TDS appears when you record a payment as “settles the invoice” and the client paid less than the balance.</span></div>
          ) : (
            <div className="v2i-rows">
              {shown.map((e) => (
                <div key={e._id} className="v2t-row">
                  <span className="v2pub-day"><strong>{e.quarter || "—"}</strong><span>{new Date(e.paymentDate).toLocaleDateString("en-IN", { month: "short" })}</span></span>
                  <span className="v2-row-main">
                    <span className="v2-row-title">{e.clientId?.clientName || e.clientName || "Client"}</span>
                    <span className="v2-row-sub">{e.invoiceNumber} · paid {fmtDate(e.paymentDate)} · invoice {inr(e.invoiceAmount, { decimals: 2 })}</span>
                  </span>
                  <span className="v2t-amt">
                    <b>{inr(e.tdsAmount, { decimals: 2 })}</b>
                    {e.tdsRate ? <span>{Number(e.tdsRate).toFixed(e.tdsRate % 1 ? 2 : 0)}%</span> : null}
                  </span>
                  <span className="v2t-cert">
                    {e.certificateReceived ? (
                      <span className="v2-status v2-status--good" title={e.certificateNumber || ""}><Glyph name="check" size={13} stroke={2.2} /> {e.certificateNumber || "Received"}</span>
                    ) : canWrite ? (
                      <button type="button" className="v2-btn v2-btn--sm" onClick={() => setCertFor(e)}>Mark received</button>
                    ) : <span className="v2-status v2-status--warn"><Glyph name="clock" size={13} stroke={2.2} /> Pending</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
      </section>

      <CertSheet entry={certFor} onClose={() => setCertFor(null)}
        onDone={() => { setCertFor(null); showAlert?.("Certificate recorded", "success"); load(); }} />
    </div>
  );
}

function CertSheet({ entry, onClose, onDone }) {
  const [num, setNum] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [keep, setKeep] = useState(null);
  useEffect(() => { if (entry) { setKeep(entry); setNum(""); setErr(""); setDate(new Date().toISOString().split("T")[0]); } }, [entry]);
  const e = entry || keep;
  if (!e) return null;
  const save = async (ev) => {
    ev.preventDefault();
    if (!num.trim()) { setErr("Enter the certificate number from Form 16A."); return; }
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API_BASE}/api/tds/certificate/${e._id}`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ certificateNumber: num.trim(), certificateDate: date }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || "Couldn't save"); }
      onDone();
    } catch (x) { setErr(x.message); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={!!entry} onClose={onClose} title="TDS certificate received" subtitle={`${e.invoiceNumber} · ${inr(e.tdsAmount, { decimals: 2 })}`} width={460}
      footer={<><button type="button" className="v2-btn" onClick={onClose}>Cancel</button>
        <button type="submit" form="v2t-cert" className="v2-btn v2-btn--primary" disabled={busy}>{busy ? "Saving…" : "Save"}</button></>}>
      <form id="v2t-cert" className="v2-form" onSubmit={save}>
        <Field label="Certificate number" hint="As printed on Form 16A (TRACES)">
          <input className="v2-input v2-mono" autoFocus value={num} onChange={(ev) => setNum(ev.target.value.toUpperCase())} placeholder="e.g. ABCDE1234F" />
        </Field>
        <Field label="Received on"><input className="v2-input" type="date" value={date} onChange={(ev) => setDate(ev.target.value)} /></Field>
        {err && <div className="v2-banner v2-banner--bad"><Glyph name="alert" size={16} /> {err}</div>}
      </form>
    </Sheet>
  );
}
